<template>
  <div class="article-page">
    <sub-nav :items="subNavItems" />

    <section class="article">
      <div class="article__inner">
        <h1 class="article__title">{{ title }}</h1>

        <div v-if="image" class="article__hero">
          <img :src="image" :alt="title" class="article__hero-img" />
        </div>

        <template v-if="hasCruiseList">
          <div class="article__body">
            <time class="article__date">{{ date }}</time>
            <div class="article__content" v-html="contentBefore"></div>
          </div>
          <div id="newsDetailCruiseList" class="article__cruise-list"></div>
          <div v-if="contentAfter" class="article__body">
            <div class="article__content" v-html="contentAfter"></div>
          </div>
        </template>
        <div v-else class="article__body">
          <time class="article__date">{{ date }}</time>
          <div class="article__content" v-html="content"></div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import SubNav from "../components/SubNav.vue";

var CRUISE_LIST_MARKER = '<div id="newsDetailCruiseList"></div>'

export default {
  name: "NewsDetailPage",
  components: { SubNav },
  props: {
    title: { type: String, default: "" },
    date: { type: String, default: "" },
    image: { type: String, default: "" },
    content: { type: String, default: "" },
    subNavItems: { type: Array, default: () => [] },
  },
  computed: {
    // Бэк-шаблон через str_replace подменяет в DETAIL_TEXT placeholder #cruise_list# на маунт-див.
    // Если такой див есть — рендерим его СНАРУЖИ .article__content, иначе deep-селекторы статьи
    // (article__content >>> p / ul / ol) ломают вёрстку RouteCard.
    cruiseListIdx: function () {
      if (!this.content) return -1
      return this.content.indexOf(CRUISE_LIST_MARKER)
    },
    hasCruiseList: function () { return this.cruiseListIdx >= 0 },
    contentBefore: function () {
      return this.hasCruiseList ? this.content.slice(0, this.cruiseListIdx) : ''
    },
    contentAfter: function () {
      return this.hasCruiseList
        ? this.content.slice(this.cruiseListIdx + CRUISE_LIST_MARKER.length)
        : ''
    },
  },
};
</script>

<style scoped>
.article-page {
  background-color: var(--color-bg);
  padding-bottom: 150px;
}

@media (max-width: 1024px) {
  .article-page {
    padding-bottom: 80px;
  }
}

@media (max-width: 480px) {
  .article-page {
    padding-bottom: 60px;
  }
}

.article__inner {
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 0 var(--container-padding);
}

.article__title {
  font-family: var(--font-family-display);
  font-size: var(--font-size-3xl);
  font-weight: 300;
  color: var(--color-text);
  line-height: 1.1;
  text-align: center;
  margin-top: 40px;
  margin-bottom: 40px;
}

.article__hero {
  margin-bottom: 48px;
}

.article__hero-img {
  display: block;
  width: 100%;
  height: auto;
}

.article__body {
  max-width: 744px;
}

.article__date {
  display: block;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  color: var(--color-text-muted);
  margin-bottom: 12px;
}

.article__cruise-list {
  margin: 32px 0;
}

.article__content >>> h2 {
  font-family: var(--font-family-display);
  font-size: 46px;
  font-weight: 400;
  color: var(--color-text);
  line-height: 1.1;
  margin-bottom: 24px;
}

.article__content >>> h3 {
  font-size: 20px;
  font-weight: 400;
  color: var(--color-text);
  margin-bottom: 20px;
}

.article__content >>> p {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-light);
  color: var(--color-text);
  line-height: var(--line-height-relaxed);
  margin-bottom: 32px;
}

.article__content >>> ul,
.article__content >>> ol {
  max-width: 720px;
  margin-bottom: 48px;
  padding-left: 20px;
}
.article__content >>> ul { list-style-type: disc; }
.article__content >>> ol { list-style-type: decimal; }

.article__content >>> ul li,
.article__content >>> ol li {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-light);
  color: var(--color-text);
  line-height: var(--line-height-normal);
  margin-bottom: 8px;
}

.article__content >>> img {
  max-width: 100%;
  height: auto;
  margin: 24px 0;
}

.article__content >>> a,
.article__content >>> a:link,
.article__content >>> a:visited,
.article__content >>> a * {
  color: var(--color-navy) !important;
  text-decoration: underline !important;
  transition: none;
}

.article__content >>> a:hover,
.article__content >>> a:hover * {
  text-decoration: none !important;
}

.article__content >>> font {
  color: inherit !important;
}

/* Tables */
.article__content >>> table {
  width: 100%;
  border-collapse: collapse;
  margin: 24px 0 32px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-light);
  color: var(--color-text);
  line-height: var(--line-height-normal);
}

.article__content >>> thead {
  background-color: var(--color-navy);
  color: var(--color-bg);
}

.article__content >>> thead th,
.article__content >>> thead td {
  font-weight: var(--font-weight-regular);
  color: var(--color-bg);
}

.article__content >>> th,
.article__content >>> td {
  padding: 12px 16px;
  border: 1px solid var(--color-border-light);
  text-align: left;
  vertical-align: top;
}

.article__content >>> tbody tr:nth-child(even) {
  background-color: rgba(16, 34, 64, 0.03);
}

.article__content >>> tbody tr:hover {
  background-color: rgba(16, 34, 64, 0.06);
}

.article__content >>> table p {
  margin: 0;
}

@media (max-width: 768px) {
  .article__content >>> table {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

/* Tablet */
@media (max-width: 1024px) {
  .article__inner {
    padding: 0 27px;
  }
  .article__title {
    font-size: 48px;
    text-align: center;
  }
  .article__hero-img {
    height: 438px;
  }
  .article__body {
    max-width: 714px;
  }
  .article__content >>> h2 {
    font-size: 40px;
  }
}

/* Mobile */
@media (max-width: 480px) {
  .article__inner {
    padding: 0 16px;
  }
  .article__title {
    font-size: 38px;
  }
  .article__hero-img {
    height: 201px;
  }
  .article__body {
    max-width: 100%;
  }
  .article__content >>> h2 {
    font-size: 26px;
  }
  .article__content >>> ul {
    max-width: 100%;
  }
}
</style>
